import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Input, Button } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { postRequest } from '../utilities/apicalls';
import { parseApiError, parseFormError } from '../utilities/errorHandler';
import ToastMessage from '../components/ToastMessage';
import '../styles/AdminLogin.css';
import '../styles/SharedLayout.css';

/**
 * AdminLogin Component
 * 
 * Provides admin login functionality with username and password authentication
 * @returns {JSX.Element} The AdminLogin component
 */
const AdminLogin = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  // Configure toast message settings
  ToastMessage.config({
    top: 100,
    duration: 3,
    maxCount: 1
  });

  /**
   * Handle form submission for admin login
   * @param {Object} values - Form values containing username and password
   */
  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Validate inputs before API call
      if (!values.username.trim()) {
        ToastMessage.warning('Username cannot be empty');
        return;
      }

      if (!values.password) {
        ToastMessage.warning('Password cannot be empty');
        return;
      }

      // Show warning for short passwords
      if (values.password.length < 6) {
        ToastMessage.warning('Your password seems too short. Please ensure it meets the minimum requirements.');
      }

      // Make API call to login endpoint
      const response = await postRequest('login', {
        username: values.username,
        password: values.password
      });
      
      if (response.status === 200 && response.data) {
        // Store user information from response
        localStorage.setItem('username', values.username);
        localStorage.setItem('id', response.data.id);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('requester', response.data.id);
        
        ToastMessage.success('Login successful');
        
        // Redirect to the admin acknowledgments page after a short delay
        setTimeout(() => {
          history.push('/admin-acknowledgment-receipt');
        }, 1000);
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Authentication failed. Please check your credentials and try again.';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // console.error('Login error:', error);
      if (error.errorFields) {
        // Form validation errors
        ToastMessage.error(parseFormError(error.errorFields));
      } else {
        // API errors
        const errorMessage = error?.messages?.error_messages?.[0] || parseApiError(error);
        ToastMessage.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <main className="body">
        <div className="container">
          {/* Company Info - Moved to top */}
          <div className="company-info">
            <img 
              className="myt-logo" 
              src={`${process.env.PUBLIC_URL}/images/MYT Logo.png`} 
              alt="MYT Logo" 
            />
            <span className="company-title">MYT Softdev Solutions Inc.</span>
          </div>

          <div className="card">
            {/* Header Section */}
            <header className="header">
              <div className="logo-container">
                <img 
                  className="logo" 
                  src={`${process.env.PUBLIC_URL}/images/Smart Forms Logo.png`} 
                  alt="Smart Forms Logo" 
                />
              </div>
              <h2 className="admin-portal">Admin Portal</h2>
            </header>

            {/* Form Section */}
            <Form
              form={form}
              name="admin_login"
              className="form"
              layout="vertical"
              onFinish={onFinish}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: 'Please input your username' }]}
              >
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder="Enter your username"
                  size="large"
                  className="username-input"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Please input your password' }]}
              >
                <Input.Password
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  placeholder="Enter your password"
                  iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  size="large"
                  className="password-input"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                className="login-button"
                block
                loading={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <div className="divider">or</div>

              <Button
                type="default"
                icon={<LockOutlined className="scholar-icon" />}
                className="scholar-button"
                block
                onClick={() => history.push('/scholar-login')}
              >
                <span className="scholar-text">Login as Scholar</span>
              </Button>
            </Form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin; 